import * as React from 'react';
import {
    List,
    Datagrid,
    TextField,
    BooleanField,
    NumberField,
    DateField,
    SearchInput,
    SelectInput,
    TopToolbar,
    FilterButton,
    CreateButton,
    Pagination,
    DatagridLoading,
    WithListContext,
    useTranslate,
} from 'react-admin';
import { Chip } from '@mui/material';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import LockOpenIcon from '@mui/icons-material/LockOpen';

const statusChoices = [
    { id: 0, name: 'Active' },
    { id: 1, name: 'Deleted' },
];

const ApiConfigFilters = [
    <SearchInput source="q" alwaysOn />,
    <SelectInput source="status" choices={statusChoices} />,
];

const ListActions = () => (
    <TopToolbar>
        <FilterButton />
        <CreateButton />
    </TopToolbar>
);

const StatusField = ({ record }: { record?: any }) => {
    if (!record) return null;
    const status = record.status;
    return (
        <Chip
            label={status === 0 ? 'Active' : 'Deleted'}
            size="small"
            color={status === 0 ? 'success' : 'default'}
            variant="outlined"
        />
    );
};

interface ApiConfigListProps {
    embedded?: boolean;
}

const ApiConfigList = ({ embedded }: ApiConfigListProps) => {
    const translate = useTranslate();

    const listProps = embedded
        ? {
              resource: 'admin/api-configs',
              storeKey: 'apiConfigListEmbedded',
              disableSyncWithLocation: true,
          }
        : {};

    return (
        <List
            resource="admin/api-configs"
            filters={ApiConfigFilters}
            sort={{ field: 'application_name', order: 'ASC' }}
            perPage={50}
            pagination={<Pagination rowsPerPageOptions={[25, 50, 100]} />}
            actions={<ListActions />}
            {...listProps}
        >
            <WithListContext
                render={({ isLoading, isFetching }) =>
                    isLoading || isFetching ? (
                        <DatagridLoading nbChildren={7} nbFakeLines={8} />
                    ) : (
                        <Datagrid rowClick="edit">
                            <TextField
                                source="application_name"
                                label={translate(
                                    'hallinta.api_configs.fields.application_name'
                                )}
                            />
                            <TextField
                                source="key"
                                label={translate(
                                    'hallinta.api_configs.fields.key'
                                )}
                            />
                            <TextField
                                source="name"
                                label={translate(
                                    'hallinta.api_configs.fields.name'
                                )}
                            />
                            <TextField
                                source="type"
                                label={translate(
                                    'hallinta.api_configs.fields.type'
                                )}
                            />
                            <TextField
                                source="role"
                                label={translate(
                                    'hallinta.api_configs.fields.role'
                                )}
                            />
                            <TextField
                                source="jwt_algorithm"
                                label={translate(
                                    'hallinta.api_configs.fields.jwt_algorithm'
                                )}
                            />
                            <TextField
                                source="jwt_ttl"
                                label={translate(
                                    'hallinta.api_configs.fields.jwt_ttl'
                                )}
                            />
                            <BooleanField
                                source="is_default"
                                label={translate(
                                    'hallinta.api_configs.fields.is_default'
                                )}
                            />
                            <NumberField
                                source="jwt_keys_count"
                                label={
                                    <VpnKeyIcon
                                        fontSize="small"
                                        titleAccess="JWT Keys"
                                    />
                                }
                            />
                            <NumberField
                                source="oauth_clients_count"
                                label={
                                    <LockOpenIcon
                                        fontSize="small"
                                        titleAccess="OAuth Clients"
                                    />
                                }
                            />
                            <StatusField />
                        </Datagrid>
                    )
                }
            />
        </List>
    );
};

export default ApiConfigList;
